import React, {useCallback, useMemo} from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from "@material-ui/core";
import Autocomplete from "@material-ui/lab/Autocomplete";
import ArchiveIcon from "@material-ui/icons/Archive";
import {formatDate} from "common/utils/format_date";
import {Definition} from "common/flowmaster/types/data_asset";

const styles = {
  menuItem: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
    gap: ".5em",
  },
  menuItemName: {
    flexGrow: 1,
    display: "flex",
    alignItems: "center",
    gap: ".5em",
  },
  menuItemDate: {
    fontSize: "0.75rem",
    color: "#75757A",
    alignSelf: "center",
    display: "flex",
    alignItems: "center",
  },
  selectedValue: {
    display: "flex",
    justifyContent: "space-between",
    width: "100%",
    alignItems: "center",
    gap: ".5em",
  },
  selectedValueName: {
    display: "flex",
    alignItems: "center",
    gap: ".5em",
  },
  selectedValueDate: {
    fontSize: "0.75rem",
    color: "#75757A",
  },
  archiveIcon: {
    fontSize: "16px",
    color: "#757575",
    marginLeft: "4px",
  },
} as const;

interface DefinitionGroup {
  groupId: string;
  latest: Definition;
  items: Definition[];
}

interface DefinitionSelectorProps {
  definitions: Definition[];
  selectedDefinitionId: number | null;
  handleDefinitionChange: (definitionId: number) => void;
  formControlStyles?: React.CSSProperties;
  fullWidth?: boolean;
}

const DefinitionSelector: React.FC<DefinitionSelectorProps> = ({
  definitions,
  selectedDefinitionId,
  handleDefinitionChange,
  formControlStyles,
  fullWidth,
}) => {
  const selectedDefinition = useMemo(() => {
    return definitions.find(d => d.id === Number(selectedDefinitionId));
  }, [definitions, selectedDefinitionId]);

  const definitionsForGrouping = useMemo(() => {
    const activeDefinitions = definitions.filter(def => !def.is_archived);

    if (selectedDefinition?.is_archived) {
      return [...activeDefinitions, selectedDefinition];
    }

    return activeDefinitions;
  }, [definitions, selectedDefinition]);

  const definitionGroups = useMemo((): DefinitionGroup[] => {
    const grouped = definitionsForGrouping.reduce(
      (acc: Record<string, Definition[]>, definition: Definition) => {
        const groupId = definition.group_id || definition.id.toString();
        if (!acc[groupId]) {
          acc[groupId] = [];
        }
        acc[groupId].push(definition);
        return acc;
      },
      {} as Record<string, Definition[]>,
    );

    Object.keys(grouped).forEach(groupId => {
      grouped[groupId].sort(
        (a, b) =>
          new Date(b.creation_date || 0).getTime() -
          new Date(a.creation_date || 0).getTime(),
      );
    });

    return Object.entries(grouped)
      .map(([groupId, items]: [string, Definition[]]) => ({
        groupId,
        latest: items[0],
        items,
      }))
      .sort((a, b) => a.latest.name.localeCompare(b.latest.name));
  }, [definitionsForGrouping]);

  const currentGroup = useMemo(() => {
    if (!selectedDefinition) {
      return null;
    }

    const groupId =
      selectedDefinition.group_id || selectedDefinition.id.toString();

    const group = definitionGroups.find(g => g.groupId === groupId);
    if (group) {
      return group;
    }

    const groupDefinitions = definitions.filter(
      d => (d.group_id || d.id.toString()) === groupId,
    );

    groupDefinitions.sort(
      (a, b) =>
        new Date(b.creation_date || 0).getTime() -
        new Date(a.creation_date || 0).getTime(),
    );

    return {
      groupId,
      latest: groupDefinitions[0],
      items: groupDefinitions,
    };
  }, [definitionGroups, selectedDefinition, definitions]);

  const handleGroupChange = useCallback(
    (_: React.ChangeEvent<object>, newValue: DefinitionGroup | null) => {
      if (newValue) {
        handleDefinitionChange(newValue.latest.id);
      }
    },
    [handleDefinitionChange],
  );

  const handleVersionChange = useCallback(
    (event: React.ChangeEvent<{value: unknown}>) => {
      const definitionId = event.target.value as number;
      handleDefinitionChange(definitionId);
    },
    [handleDefinitionChange],
  );

  const renderVersionSelectValue = useCallback(
    (selected: unknown) => {
      const definition = definitions.find(d => d.id === Number(selected));

      if (!definition) {
        return "";
      }

      return (
        <div style={styles.selectedValue}>
          <div style={styles.selectedValueName}>
            <span>{definition.name}</span>
            {definition.is_archived && (
              <ArchiveIcon style={styles.archiveIcon} />
            )}
          </div>
          <span style={styles.selectedValueDate}>
            {formatDate(definition.creation_date)}
          </span>
        </div>
      );
    },
    [definitions],
  );

  return (
    <>
      <Autocomplete
        style={formControlStyles}
        fullWidth={fullWidth}
        options={definitionGroups}
        getOptionLabel={group => group.latest.name}
        value={currentGroup}
        onChange={handleGroupChange}
        disabled={definitionGroups.length === 0}
        renderInput={params => (
          <TextField {...params} label="Select Definition" variant="outlined" />
        )}
        renderOption={group => (
          <div style={styles.menuItem}>
            <span style={styles.menuItemName}>{group.latest.name}</span>
            <span style={styles.menuItemDate}>
              {group.items.length} version
              {group.items.length !== 1 ? "s" : ""}
            </span>
          </div>
        )}
      />

      <FormControl
        variant="outlined"
        style={formControlStyles}
        fullWidth={fullWidth}
      >
        <InputLabel>Version</InputLabel>
        <Select
          value={selectedDefinitionId || ""}
          onChange={handleVersionChange}
          label="Version"
          disabled={!currentGroup || currentGroup.items.length === 0}
          renderValue={renderVersionSelectValue}
          MenuProps={{
            PaperProps: {
              style: {
                maxHeight: 300,
                overflowY: "auto",
              },
            },
            anchorOrigin: {
              vertical: "bottom",
              horizontal: "left",
            },
            transformOrigin: {
              vertical: "top",
              horizontal: "left",
            },
            getContentAnchorEl: null,
          }}
        >
          {currentGroup?.items.map(definition => (
            <MenuItem key={definition.id} value={definition.id}>
              <div style={styles.menuItem}>
                <div style={styles.menuItemName}>
                  <span>{definition.name}</span>
                  {definition.is_archived && (
                    <ArchiveIcon style={styles.archiveIcon} />
                  )}
                </div>
                <span style={styles.menuItemDate}>
                  {formatDate(definition.creation_date)}
                </span>
              </div>
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </>
  );
};

export default DefinitionSelector;
